/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Plus,
  Search,
  Loader2,
  Edit2,
  Trash2,
  Truck,
  AlertCircle,
  AlertTriangle,
  ArrowUpDown,
  MapPin,
  X,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  fetchDeliverySlabs,
  createDeliverySlab,
  updateDeliverySlab,
  deleteDeliverySlab,
  validateDeliverySlab,
} from './api/DeliverySlabApi';
import { useUser } from '@/contexts/UserContext';
import BottomNav from '../../components/layouts/Footer/BottomNav';

const defaultFormData = {
  minKm: '',
  maxKm: '',
  charge: '',
};

export default function DeliverySlabManagement() {
  const navigate = useNavigate();
  const { user, isInitialized } = useUser();

  const [slabs, setSlabs] = useState([]);
  const [filteredSlabs, setFilteredSlabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [editingSlab, setEditingSlab] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [formData, setFormData] = useState(defaultFormData);

  const vendorId = user?.id;

  // Fetch delivery slabs
  const fetchSlabs = async (id) => {
    if (!id) return;

    try {
      setLoading(true);
      const data = await fetchDeliverySlabs(id);
      setSlabs(Array.isArray(data) ? data : []);
      setFilteredSlabs(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to fetch delivery slabs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Load slabs on component mount and when vendorId changes
  useEffect(() => {
    if (isInitialized && vendorId) {
      fetchSlabs(vendorId);
    }
  }, [vendorId, isInitialized]);

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Create delivery slab
  const handleCreateSlab = async () => {
    if (!vendorId) {
      toast.error('Vendor ID not found');
      return;
    }

    const newSlab = {
      minKm: parseFloat(formData.minKm),
      maxKm: parseFloat(formData.maxKm),
      charge: parseFloat(formData.charge),
    };

    const validation = validateDeliverySlab(newSlab, slabs);
    if (!validation.isValid) {
      toast.error(validation.message);
      return;
    }

    try {
      const fullList = [...slabs, newSlab];
      await createDeliverySlab(vendorId, fullList);
      toast.success('Delivery slab created successfully');
      setIsAddDialogOpen(false);
      setFormData(defaultFormData);
      fetchSlabs(vendorId);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to create delivery slab');
    }
  };

  // Update delivery slab
  const handleUpdateSlab = async () => {
    if (!editingSlab || !vendorId) {
      toast.error('Slab or vendor ID not found');
      return;
    }

    const updatedSlab = {
      minKm: parseFloat(formData.minKm),
      maxKm: parseFloat(formData.maxKm),
      charge: parseFloat(formData.charge),
    };

    const otherSlabs = slabs.filter((s) => s.id !== editingSlab.id);
    const validation = validateDeliverySlab(updatedSlab, otherSlabs, editingSlab.id);
    if (!validation.isValid) {
      toast.error(validation.message);
      return;
    }

    try {
      await updateDeliverySlab(vendorId, editingSlab.id, updatedSlab);
      toast.success('Delivery slab updated successfully');
      setIsEditDialogOpen(false);
      setFormData(defaultFormData);
      setEditingSlab(null);
      fetchSlabs(vendorId);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update delivery slab');
    }
  };

  // Delete delivery slab
  const handleDeleteSlab = async (slabId) => {
    if (!vendorId) {
      toast.error('Vendor ID not found');
      return;
    }

    try {
      await deleteDeliverySlab(vendorId, slabId);
      toast.success('Delivery slab deleted successfully');
      setIsDeleteDialogOpen(false);
      setDeleteConfirmId(null);
      fetchSlabs(vendorId);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete delivery slab');
    }
  };

  // Open edit dialog
  const openEditDialog = (slab) => {
    setEditingSlab(slab);
    setFormData({
      minKm: slab.minKm.toString(),
      maxKm: slab.maxKm.toString(),
      charge: slab.charge.toString(),
    });
    setIsEditDialogOpen(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData(defaultFormData);
    setEditingSlab(null);
  };

  // Filter slabs by search query
  useEffect(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      setFilteredSlabs([...slabs]);
    } else {
      const filtered = slabs.filter(
        (slab) =>
          slab.minKm.toString().includes(query) ||
          slab.maxKm.toString().includes(query) ||
          slab.charge.toString().includes(query)
      );
      setFilteredSlabs(filtered);
    }
  }, [searchQuery, slabs]);

  // Handle sort
  const handleSort = () => {
    const sorted = [...filteredSlabs].sort((a, b) => {
      const comparison = a.minKm - b.minKm;
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    setFilteredSlabs(sorted);
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!filteredSlabs.length) return [];

    const sorted = [...filteredSlabs].sort((a, b) => a.minKm - b.minKm);
    const data = [];

    // Add start point
    data.push({
      distance: sorted[0].minKm,
      charge: sorted[0].charge,
      label: `${sorted[0].minKm.toFixed(1)}km`,
    });

    // Add end points for each slab
    sorted.forEach((slab) => {
      data.push({
        distance: slab.maxKm,
        charge: slab.charge,
        label: `${slab.maxKm.toFixed(1)}km`,
      });
    });

    return data;
  }, [filteredSlabs]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
                  <Truck className="w-6 h-6 text-white" />
                </div>
                Delivery Slab Management
              </h1>
              <p className="text-sm text-gray-500 mt-2">
                Manage distance-based delivery charges for your customers
              </p>
            </div>
            {vendorId && (
              <Button
                onClick={() => setIsAddDialogOpen(true)}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-md hover:shadow-lg transition-all duration-200"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Slab
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8 pb-20 md:pb-8">
        {/* Search and Sort */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
            <Input
              placeholder="Search slabs by distance or charge..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 border-slate-300 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <Button
            onClick={handleSort}
            variant="outline"
            className="border-slate-300 text-gray-700 hover:bg-slate-50"
          >
            <ArrowUpDown className="h-4 w-4 mr-2" />
            Sort ({sortOrder === 'asc' ? 'Low' : 'High'})
          </Button>
        </div>

        {/* Two Column Layout: Table + Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Slabs Table */}
          <div className="lg:col-span-2">
            <Card className="shadow-lg border-slate-200">
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex justify-center items-center py-16">
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
                      <p className="text-gray-500 font-medium">Loading delivery slabs...</p>
                    </div>
                  </div>
                ) : filteredSlabs.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                            Distance Range
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                            Min (km)
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                            Max (km)
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                            Charge (₹)
                          </th>
                          <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {filteredSlabs.map((slab) => (
                          <tr
                            key={slab.id}
                            className="hover:bg-slate-50 transition-colors duration-150"
                          >
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                <span className="font-semibold text-gray-900">
                                  {slab.minKm.toFixed(2)} - {slab.maxKm.toFixed(2)} km
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-sm text-gray-700">{slab.minKm.toFixed(2)}</p>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-sm text-gray-700">{slab.maxKm.toFixed(2)}</p>
                            </td>
                            <td className="px-6 py-4">
                              <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 font-semibold">
                                ₹{slab.charge.toFixed(2)}
                              </Badge>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => openEditDialog(slab)}
                                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-150"
                                  title="Edit slab"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setDeleteConfirmId(slab.id);
                                    setIsDeleteDialogOpen(true);
                                  }}
                                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-150"
                                  title="Delete slab"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="py-16 text-center">
                    <div className="mb-4 flex justify-center">
                      <div className="p-3 bg-blue-50 rounded-full">
                        <MapPin className="w-8 h-8 text-blue-300" />
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      No delivery slabs found
                    </h3>
                    <p className="text-gray-500 mb-6">
                      {searchQuery
                        ? 'Try adjusting your search terms'
                        : 'Create your first delivery slab to get started'}
                    </p>
                    {!searchQuery && vendorId && (
                      <Button
                        onClick={() => setIsAddDialogOpen(true)}
                        className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Create First Slab
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right: Line Chart */}
          {!loading && filteredSlabs.length > 0 && (
            <div className="lg:col-span-1">
              <Card className="shadow-lg border-slate-200 sticky top-24">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-50 border-b border-slate-200 py-3">
                  <CardTitle className="text-sm font-semibold text-gray-900">
                    Delivery Charge Trend
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="#6b7280" />
                      <YAxis
                        tick={{ fontSize: 12 }}
                        stroke="#6b7280"
                        label={{ value: '₹', angle: -90, position: 'insideLeft', offset: 10 }}
                      />
                      <Tooltip
                        formatter={(value) => `₹${value.toFixed(2)}`}
                        labelFormatter={(label) => label}
                        contentStyle={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.5rem',
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="charge"
                        stroke="#3b82f6"
                        strokeWidth={3}
                        dot={{ fill: '#3b82f6', r: 5 }}
                        activeDot={{ r: 7 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>

                  <div className="mt-4 pt-4 border-t border-gray-200 space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Slabs</span>
                      <span className="font-semibold text-gray-900">{filteredSlabs.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Min Distance</span>
                      <span className="font-semibold text-gray-900">
                        {Math.min(...filteredSlabs.map((s) => s.minKm)).toFixed(1)} km
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Max Distance</span>
                      <span className="font-semibold text-gray-900">
                        {Math.max(...filteredSlabs.map((s) => s.maxKm)).toFixed(1)} km
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Avg Charge</span>
                      <span className="font-semibold text-gray-900">
                        ₹
                        {(
                          filteredSlabs.reduce((sum, s) => sum + s.charge, 0) / filteredSlabs.length
                        ).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Add Slab Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg">Add New Delivery Slab</DialogTitle>
            <DialogDescription>
              Create a new distance-based delivery charge slab. Ranges cannot overlap.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="minKm" className="text-sm font-semibold text-gray-700">
                Minimum Distance (km) *
              </Label>
              <Input
                id="minKm"
                name="minKm"
                type="number"
                step="0.01"
                min="0"
                value={formData.minKm}
                onChange={handleInputChange}
                placeholder="0.00"
                className="mt-1 border-slate-300 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <Label htmlFor="maxKm" className="text-sm font-semibold text-gray-700">
                Maximum Distance (km) *
              </Label>
              <Input
                id="maxKm"
                name="maxKm"
                type="number"
                step="0.01"
                min="0"
                value={formData.maxKm}
                onChange={handleInputChange}
                placeholder="10.00"
                className="mt-1 border-slate-300 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <Label htmlFor="charge" className="text-sm font-semibold text-gray-700">
                Delivery Charge (₹) *
              </Label>
              <Input
                id="charge"
                name="charge"
                type="number"
                step="0.01"
                min="0"
                value={formData.charge}
                onChange={handleInputChange}
                placeholder="0.00"
                className="mt-1 border-slate-300 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-start gap-2 text-sm bg-blue-50 border border-blue-200 text-blue-800 p-3 rounded-lg">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p>
                Overlapping distance ranges are not allowed. Make sure your minimum and maximum
                distances don't overlap with existing slabs.
              </p>
            </div>

            <DialogFooter className="gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsAddDialogOpen(false);
                  resetForm();
                }}
                className="flex-1 border-slate-300 text-gray-700 hover:bg-slate-50"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleCreateSlab}
                className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-md hover:shadow-lg"
              >
                <Check className="w-4 h-4 mr-2" />
                Create Slab
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Slab Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg">Edit Delivery Slab</DialogTitle>
            <DialogDescription>
              Update the distance range and delivery charge for this slab.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-minKm" className="text-sm font-semibold text-gray-700">
                Minimum Distance (km) *
              </Label>
              <Input
                id="edit-minKm"
                name="minKm"
                type="number"
                step="0.01"
                min="0"
                value={formData.minKm}
                onChange={handleInputChange}
                placeholder="0.00"
                className="mt-1 border-slate-300 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <Label htmlFor="edit-maxKm" className="text-sm font-semibold text-gray-700">
                Maximum Distance (km) *
              </Label>
              <Input
                id="edit-maxKm"
                name="maxKm"
                type="number"
                step="0.01"
                min="0"
                value={formData.maxKm}
                onChange={handleInputChange}
                placeholder="10.00"
                className="mt-1 border-slate-300 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <Label htmlFor="edit-charge" className="text-sm font-semibold text-gray-700">
                Delivery Charge (₹) *
              </Label>
              <Input
                id="edit-charge"
                name="charge"
                type="number"
                step="0.01"
                min="0"
                value={formData.charge}
                onChange={handleInputChange}
                placeholder="0.00"
                className="mt-1 border-slate-300 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-start gap-2 text-sm bg-blue-50 border border-blue-200 text-blue-800 p-3 rounded-lg">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p>Verify that the updated range doesn't overlap with other slabs.</p>
            </div>

            <DialogFooter className="gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsEditDialogOpen(false);
                  resetForm();
                }}
                className="flex-1 border-slate-300 text-gray-700 hover:bg-slate-50"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleUpdateSlab}
                className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-md hover:shadow-lg"
              >
                <Check className="w-4 h-4 mr-2" />
                Update Slab
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div className="text-center">
              <DialogTitle className="text-lg">Delete Delivery Slab</DialogTitle>
              <DialogDescription className="mt-2">
                Are you sure you want to delete this delivery slab? This action cannot be undone.
              </DialogDescription>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setDeleteConfirmId(null);
              }}
              className="flex-1 border-slate-300 text-gray-700 hover:bg-slate-50"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleDeleteSlab(deleteConfirmId)}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
}
